import { defineStore, storeToRefs } from 'pinia';
import { useState } from '@/composables/common/useState';
import Vue, { computed } from 'vue';
import i18n from '@/i18n';
import { useToggle } from '@/composables/useToggle';
import vuetify from '@/plugins/vuetify';
import OrganizationService from '@/services/coda-qa-user/organization.service';
import CfsLogo from '@/assets/img/products/cfs_named_logo.svg';

import CodaLogo from '../../../favicon.ico';
import CCWLogo from '@/assets/img/products/ccw_named_logo.png';
import LogiwareLogo from '@/assets/img/products/logiware_logo.svg';
import {
  PermissionsCustomsFeatures,
  PermissionsEdiFeatures,
  PermissionsExportsFeatures,
  PermissionsFinanceFeatures,
  PermissionsGroupName,
  PermissionsImportFeatures,
  PermissionsReportsFeatures,
} from '@/constants/permissions';
import IpiLoadingIcon from '@/assets/img/icons/sidebar/ipi-loading.svg';
import IpiReceivingIcon from '@/assets/img/icons/sidebar/ipi-receiving.svg';
import ImportIcon from '@/assets/img/icons/sidebar/import-icon.svg';
import ExportIcon from '@/assets/img/icons/sidebar/export-icon.svg';
import CoinIcon from '@/assets/img/icons/sidebar/coin-icon.svg';
import EdiIcon from '@/assets/img/icons/sidebar/edi-icon.svg';
import CustomsIcon from '@/assets/img/icons/sidebar/customs.svg';
import { useUserStore } from '@/store/modules/user/useUserStore';
import { useOrganizationStore } from './useOrganizationStore';

export const useUiStore = defineStore('UI', () => {
  const organizationStore = useOrganizationStore();
  const { permissions } = storeToRefs(useUserStore());

  const [drawer, setDrawer] = useState<boolean>(!vuetify.framework.breakpoint.xs);
  const [logoSrc, setLogoSrc] = useState<null | string>(CfsLogo);
  const [pageMeta, setPageMeta] = useState<any>({
    pageTitle: null,
  });

  const [isLoaderActive, toggleLoader] = useToggle();

  const pageTitle = computed(() => pageMeta.value.pageTitle);

  const setPageTitle = (value: any) => {
    if (value && value.value) {
      pageMeta.value.pageTitle = value;
    } else {
      pageMeta.value.pageTitle = {
        translate: false,
        value: 'CFS',
      };
    }
    Vue.prototype.$document.title = pageMeta.value.pageTitle.translate
      ? i18n.t(pageMeta.value.pageTitle.value)
      : pageMeta.value.pageTitle.value;
  };

  const fetchOrganizationLogo = async () => {
    try {
      const {
        data: { logo },
      } = await OrganizationService.getLogo();
      setLogoSrc(logo);
    } catch {
      setLogoSrc(CfsLogo);
    } finally {
      const faviconMap = {
        coda: CodaLogo,
        ccw: CCWLogo,
        logiware: LogiwareLogo,
      };

      const logoSrc =
        Object.entries(faviconMap).find(([k]) => location.hostname.startsWith(k))?.[1] ||
        LogiwareLogo;

      document.head.insertAdjacentHTML(
        'beforeend',
        `<link rel="icon" href="${logoSrc}" type="image/x-icon">`
      );
    }
  };

  const navBarCustomsItems = computed(() =>
    [
      {
        key: PermissionsCustomsFeatures.AMS,
        icon: 'mdi-checkbox-marked-circle-outline',
        text: 'AMS Status',
        to: { name: 'AmsStatus' },
      },
    ].filter((item) => {
      if (!permissions.value?.groups?.customs) {
        return false;
      }
      const { customs } = permissions.value.groups;

      return customs.features?.[item.key]?.accessType.read;
    })
  );

  const navBarImportsItems = computed(() =>
    [
      // {
      //   key: PermissionsImportFeatures.IPI_TRACKING,
      //   icon: 'mdi-crosshairs-gps',
      //   text: 'IPI Tracking',
      //   to: { name: 'IpiTracking' },
      // },
      {
        key: PermissionsImportFeatures.IPI_LOADING,
        customIcon: IpiLoadingIcon,
        text: 'IPI Loading',
        to: { name: 'IpiCalendar' },
      },
      {
        key: PermissionsImportFeatures.IPI_RECEIVING,
        customIcon: IpiReceivingIcon,
        text: 'IPI Receiving',
        to: { name: 'IpiReceiving' },
      },
      {
        key: PermissionsImportFeatures.MBL,
        icon: 'mdi-ferry',
        text: 'Manifest',
        to: { name: 'ManifestImports' },
      },
      // {
      //   key: PermissionsImportFeatures.INVENTORY,
      //   icon: 'mdi-file-cabinet',
      //   text: 'Inventory',
      //   to: { name: 'ManageInventory' },
      // },
    ].filter((item) => {
      if (!permissions.value?.groups?.imports) {
        return false;
      }
      const { imports } = permissions.value.groups;

      return imports.features?.[item.key]?.accessType.read;
    })
  );
  const navBarExportsItems = computed(() =>
    [
      {
        key: PermissionsExportsFeatures.ON_HAND,
        icon: 'mdi-cube-outline',
        text: 'On Hand',
        to: { name: 'OnHand' },
      },
      {
        key: PermissionsExportsFeatures.LOAD_PLAN,
        icon: 'mdi-calendar-text-outline',
        text: 'Load Plan',
        to: { name: 'LoadPlan' },
      },
      {
        key: PermissionsExportsFeatures.BARCODE_SCANNING,
        icon: 'mdi-file-document-outline',
        text: 'Barcode Scanning',
        to: { name: 'BarcodeScanning' },
      },
    ].filter((item) => {
      if (!permissions.value?.groups?.exports) {
        return false;
      }
      const { exports } = permissions.value.groups;
      return exports.features?.[item.key]?.accessType.read;
    })
  );

  const navBarReportItems = computed(() => [
    {
      key: PermissionsReportsFeatures.ACCOUNTING,
      icon: 'mdi-account-credit-card-outline',
      text: 'Accounting',
      to: { name: 'ReportsAccounting' },
    },
    {
      key: PermissionsReportsFeatures.ADMIN,
      icon: 'mdi-security',
      text: 'Admin',
      to: { name: 'ReportsAdmin' },
    },
    {
      key: PermissionsReportsFeatures.EXPORT,
      customIcon: ExportIcon,
      text: 'Exports',
      to: { name: 'ReportsExports' },
    },
    {
      key: PermissionsReportsFeatures.IMPORT,
      customIcon: ImportIcon,
      text: 'Imports',
      to: { name: 'ReportsImports' },
    },
    {
      key: PermissionsReportsFeatures.IPI,
      icon: 'mdi-truck-outline',
      text: 'IPI',
      to: { name: 'ReportsIPI' },
    },
  ]);

  const navBarEDIItems = computed(() =>
    [
      {
        key: PermissionsEdiFeatures.IMPORTS,
        customIcon: ImportIcon,
        text: 'Imports Ocean',
        to: { name: 'ImportsOcean' },
      },
      {
        key: PermissionsEdiFeatures.IMPORTS,
        icon: 'mdi-account-credit-card-outline',
        text: 'Imports Air',
        to: { name: 'ImportsAir' },
      },
      {
        key: PermissionsEdiFeatures.IMPORTS,
        customIcon: ExportIcon,
        text: 'Exports',
        to: { name: 'EdiExports' },
      },
    ].filter((item) => {
      if (!permissions.value?.groups?.edi) {
        return false;
      }
      const { edi } = permissions.value.groups;
      return edi.features?.[item.key]?.accessType.read;
    })
  );

  const navBarFinanceItems = computed(() =>
    [
      {
        key: PermissionsFinanceFeatures.CHARGES,
        customIcon: CoinIcon,
        text: 'Charges',
        to: { name: 'FinanceCharges' },
      },
      {
        key: PermissionsFinanceFeatures.INVOICES,
        icon: 'mdi-invoice-list-outline',
        text: 'Invoices',
        to: { name: 'FinanceInvoices' },
      },
    ].filter((item) => {
      if (!permissions.value?.groups?.finances) {
        return false;
      }
      const { finances } = permissions.value.groups;
      return finances.features?.[item.key]?.accessType.read;
    })
  );

  const filteredSidebarItems = computed(() =>
    [
      {
        key: PermissionsGroupName.EDI,
        text: 'EDI',
        customIcon: EdiIcon,
        to: { name: 'EdiDashboard' },
        children: navBarEDIItems.value,
      },
      {
        key: PermissionsGroupName.IMPORTS,
        customIcon: ImportIcon,
        text: 'Imports',
        children: navBarImportsItems.value,
      },
      {
        key: PermissionsGroupName.EXPORTS,
        customIcon: ExportIcon,
        text: 'Exports',
        children: navBarExportsItems.value,
      },
      {
        icon: 'mdi-vector-intersection',
        text: 'Loads',
        key: PermissionsGroupName.INTERFACILITY,
        children: [
          {
            icon: 'mdi-calendar-text-outline',
            text: 'Load Plans',
            to: { name: 'InterfacilityLoadPlans' },
          },
          {
            icon: 'mdi-file-document-outline',
            text: 'Load Guides',
            to: { name: 'InterfacilityLoadGuides' },
          },
        ],
      },
      {
        key: PermissionsGroupName.REPORTS,
        icon: 'mdi-file-chart-outline',
        text: 'Reports',
        children: navBarReportItems.value,
      },
      {
        key: PermissionsGroupName.CUSTOMS,
        customIcon: CustomsIcon,
        text: 'Customs',
        children: navBarCustomsItems.value,
      },
      {
        key: PermissionsGroupName.AIR,
        icon: 'mdi-airplane-takeoff',
        text: 'Air Imports',
        to: { name: 'AirImports' },
      },
      {
        key: PermissionsGroupName.PHOTOS,
        icon: 'mdi-camera-outline',
        text: 'Photos',
        to: { name: 'Photos' },
      },
      ...(organizationStore.currentWarehouse?.warehouse?.weeklyBilling?.enabled
        ? [
            {
              key: PermissionsGroupName.FINANCE,
              icon: 'mdi-cash-multiple',
              text: 'Finances',
              children: navBarFinanceItems.value,
            },
          ]
        : []),
      {
        icon: 'mdi-map-marker-multiple-outline',
        text: 'Inventory Locations',
        to: { name: 'InventoryLocations' },
        key: PermissionsGroupName.INVENTORY_LOCATIONS,
      },
    ].filter((item) => {
      return !item?.key || !!permissions.value?.groups?.[item.key]?.enabled;
    })
  );
  const isEmpire = computed(() => window.location.href.includes('empire'));
  const isHarbor = computed(() => window.location.href.includes('harborusa'));

  return {
    filteredSidebarItems,
    drawer,
    isLoaderActive,
    logoSrc,
    toggleLoader,
    setDrawer,
    setPageMeta,
    setPageTitle,
    fetchOrganizationLogo,
    pageTitle,
    isEmpire,
    isHarbor,
  };
});
